import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/attendance/shift-policy")({
  component: ShiftPolicyPage,
});

import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Loader2, Settings2, X } from "lucide-react";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/reusable-components/header/page-header";

import useFetchShiftPolicy from "@/hooks/api/shift-policy/useFetchShiftPolicy";
import useUpdateShiftPolicy from "@/hooks/api/shift-policy/useUpdateShiftPolicy";
import {
  MAX_SHIFTS_PER_DAY_CEILING,
  SHIFT_POLICY_FIELDS,
  shiftPolicySchema,
  type ShiftPolicyFormValues,
} from "@/schemas/attendance/shift-policy.schema";
import { applyServerErrors } from "@/lib/apply-server-errors";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function ShiftPolicyPage() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "attendance.shift.manage");
  const shiftPolicyQuery = useFetchShiftPolicy();
  const shiftPolicy = shiftPolicyQuery.data ?? null;
  const fetchLoading = shiftPolicyQuery.isPending;

  const updateShiftPolicy = useUpdateShiftPolicy();
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setError,
    watch,
    formState: { errors },
  } = useForm<ShiftPolicyFormValues>({
    resolver: zodResolver(shiftPolicySchema),
    defaultValues: {
      allow_multiple_daily_shifts: false,
      max_shifts_per_day: 2,
      min_gap_minutes: 0,
    },
  });

  const multipleShiftsEnabled = watch("allow_multiple_daily_shifts");

  useEffect(() => {
    if (shiftPolicy) {
      reset({
        allow_multiple_daily_shifts: shiftPolicy.allow_multiple_daily_shifts,
        max_shifts_per_day: shiftPolicy.max_shifts_per_day,
        min_gap_minutes: shiftPolicy.min_gap_minutes,
      });
    }
  }, [shiftPolicy, reset]);

  const handleFormSubmit = async (data: ShiftPolicyFormValues) => {
    try {
      await updateShiftPolicy.mutateAsync(data);
      toast.success("Shift policy updated");
      setIsEditing(false);
    } catch (err) {
      const { handled, rest } = applyServerErrors(
        err,
        setError,
        SHIFT_POLICY_FIELDS
      );
      if (!handled || rest.length > 0)
        toast.error(rest[0] ?? "Failed to update the shift policy");
    }
  };

  const handleCancel = () => {
    if (shiftPolicy) {
      reset({
        allow_multiple_daily_shifts: shiftPolicy.allow_multiple_daily_shifts,
        max_shifts_per_day: shiftPolicy.max_shifts_per_day,
        min_gap_minutes: shiftPolicy.min_gap_minutes,
      });
    }
    setIsEditing(false);
  };

  const isLoading = fetchLoading || updateShiftPolicy.isPending;

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Shift Policy"
          description="Control whether staff can be scheduled for more than one shift in a day."
          isLoading={isLoading}
        >
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit(handleFormSubmit)}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {updateShiftPolicy.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            canManage && (
              <Button
                onClick={() => setIsEditing(true)}
                disabled={isLoading}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Settings2 className="h-4 w-4" />
                Edit Settings
              </Button>
            )
          )}
        </PageHeader>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Shift Policy Configuration
            </CardTitle>
            <CardDescription className="text-justify">
              These apply across all of Attendance for this organization.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {fetchLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-full rounded" />
                <Skeleton className="h-10 w-full rounded" />
                <Skeleton className="h-10 w-full rounded" />
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                className="space-y-4"
              >
                {/* Allow Multiple Shifts Field */}
                <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="allow_multiple_daily_shifts">
                      Allow multiple shifts per day
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      A split shift is two separate blocks in one day 'say
                      06:00–10:00 and 16:00–20:00' with the staff member
                      released in between; the gap is unpaid, off-duty time.
                      When off, a second shift on the same date is refused.
                    </p>
                  </div>
                  <div className="flex w-full justify-start sm:w-auto sm:shrink-0">
                    <Controller
                      name="allow_multiple_daily_shifts"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="allow_multiple_daily_shifts"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isEditing || isLoading}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Maximum Shifts Per Day Field */}
                <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="max_shifts_per_day">
                      Maximum shifts per day
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      Most shifts assignable to one employee on one date, up to{" "}
                      {MAX_SHIFTS_PER_DAY_CEILING} blocks. Only consulted when
                      multiple daily shifts are allowed above.
                    </p>
                  </div>
                  <div className="w-full sm:w-[70px] sm:shrink-0">
                    <Input
                      id="max_shifts_per_day"
                      type="number"
                      min={1}
                      max={MAX_SHIFTS_PER_DAY_CEILING}
                      disabled={
                        !isEditing || isLoading || !multipleShiftsEnabled
                      }
                      {...register("max_shifts_per_day", {
                        valueAsNumber: true,
                      })}
                    />
                    {errors.max_shifts_per_day && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.max_shifts_per_day.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Minimum Gap Between Shifts Field */}
                <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="min_gap_minutes">
                      Minimum gap between shifts (minutes)
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      Smallest permitted off-duty gap between two shifts on the
                      same date. 0 allows back-to-back blocks, such as a shift
                      handover - blocks may never overlap.
                    </p>
                  </div>
                  <div className="w-full sm:w-[70px] sm:shrink-0">
                    <Input
                      id="min_gap_minutes"
                      type="number"
                      min={0}
                      disabled={
                        !isEditing || isLoading || !multipleShiftsEnabled
                      }
                      {...register("min_gap_minutes", {
                        valueAsNumber: true,
                      })}
                    />
                    {errors.min_gap_minutes && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.min_gap_minutes.message}
                      </p>
                    )}
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </SidebarInset>
  );
}
